/**
 * Starter Menu — wraps Mantine's `<Menu>` to thread design-system
 * surface tokens through the dropdown.
 *
 * The dropdown surface picks up --bg-surface + --shadow-2 + --radius-lg.
 * Items inherit --hover-bg, --selected-bg/--selected-fg, and the design's
 * danger-item variant via `data-danger` on `<Menu.Item>`.
 *
 * All Mantine compound parts (Target, Dropdown, Item, Label, Divider) are
 * re-exposed under the same names so existing call-sites compile.
 */
import type { ReactElement } from 'react';
import { Menu as MantineMenu, type MenuProps as MantineMenuProps } from '@mantine/core';
import styles from './Menu.module.css';

export interface MenuProps extends MantineMenuProps {}

function MenuRoot({ classNames, ...rest }: MenuProps): ReactElement {
  const existing = (classNames ?? {}) as {
    dropdown?: string;
    item?: string;
    label?: string;
    divider?: string;
  };
  const mergedClassNames = {
    ...existing,
    dropdown: [styles.dropdown, existing.dropdown].filter(Boolean).join(' '),
    item: [styles.item, existing.item].filter(Boolean).join(' '),
    label: [styles.label, existing.label].filter(Boolean).join(' '),
    divider: [styles.divider, existing.divider].filter(Boolean).join(' '),
  };

  return <MantineMenu classNames={mergedClassNames} {...rest} />;
}

export const Menu = Object.assign(MenuRoot, {
  Target: MantineMenu.Target,
  Dropdown: MantineMenu.Dropdown,
  Item: MantineMenu.Item,
  Label: MantineMenu.Label,
  Divider: MantineMenu.Divider,
});
